import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { DAY_MONTH_NAME } from 'constants/dateFormats'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'
import { useIntl } from 'react-intl'

const SizedFilterSelectBox = styled(OfferFilterDropdownBox)`
  ${mediaQueryUp.desktop} {
    min-width: ${rem(250)};
  }
`

interface Props {
  /**
   * Check in date coming directly from state
   */
  stateCheckIn?: string
  /**
   * Check out date coming directly from state
   */
  stateCheckOut?: string
  isOpen: boolean
  inSidebar?: boolean
  ref?: React.Ref<HTMLButtonElement>
  /**
   * Explicitly tell this component is a controlled component
   * and we will be passing in our own check in/check out
   */
  controlled?: boolean
  /**
   * The current check in date
   * This value will only be used if controlled is true
   */
  checkIn?: string
  /**
   * The current check out date
   * This value will only be used if controlled is true
   */
  checkOut?: string
}

function OfferDatePickerSelectBox(props: Props) {
  const intl = useIntl()
  const {
    stateCheckIn,
    stateCheckOut,
    checkIn: controlledCheckIn,
    checkOut: controlledCheckOut,
    isOpen,
    inSidebar,
    ref,
    controlled,
  } = props

  const checkIn = controlled ? controlledCheckIn : stateCheckIn
  const checkOut = controlled ? controlledCheckOut : stateCheckOut

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkOut ? moment(checkOut) : undefined, [checkOut])
  const selectedDuration = useMemo(() => checkOutMoment?.diff(checkInMoment, 'days'), [checkInMoment, checkOutMoment])
  const areDatesValid = checkIn && checkOut
  const dispatch = useOfferPageDispatch()

  const onClick = useCallback(() => {
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('CALENDAR_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      trackEvent('CALENDAR_TOGGLE_PRESSED')
    }
  }, [dispatch, isOpen, trackEvent])

  return (
    <SizedFilterSelectBox
      onClick={onClick}
      active={isOpen}
      ref={ref}
      id="date-picker"
      data-testid="OfferDatePickerSelectBox"
      icon={<LineCalendarIcon />}
      inSidebar={inSidebar}
      label={intl.formatMessage({
        id: 'A/WH4rJV4c',
        defaultMessage: 'When',
        description: 'Label for date picker filter',
      })}
      placeholder={intl.formatMessage({
        id: '+tP9xDroS5',
        defaultMessage: 'Select your dates',
        description: 'Placeholder text for date picker',
      })}
      value={areDatesValid ? <><b>{checkInMoment?.format(DAY_MONTH_NAME)} - {checkOutMoment?.format(DAY_MONTH_NAME)}</b> ({pluralizeToString('night', selectedDuration ?? 1)})</> : undefined}
    />
  )
}

type MappedProps = Pick<Props, 'stateCheckIn' | 'stateCheckOut' | 'isOpen'>

export default withOfferPageState((state): MappedProps => ({
  stateCheckIn: state.checkInDate,
  stateCheckOut: state.checkOutDate,
  isOpen: state.activeFilter === 'checkin',
}), OfferDatePickerSelectBox)
